import { useState } from 'react';
import { Link } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import { toast } from 'sonner';
import { sdk } from '@/services/sdk';
import { useAuth } from '@/layouts/RootLayout';
import { useFetch } from '@/hooks/useFetch';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { PageHeader, Pager, StatusBadge } from '@/components/kit';
import { FilterSelect, ListBody, Toolbar } from '@/components/inputs';
import { APP_CONFIG } from '@/config/app.config';
import { PAYMENT_KINDS, PAYMENT_KIND_STYLES } from '@/shared/constants';
import { PAYMENT_FIELDS } from '@/shared/fields';
import { money, paymentLabel, planName } from '@/shared/pricing';
import { displayNameOf, idOf, paged } from '@/shared/data';
import { formatLocalDate, DATE_FORMATS } from '@/utils/date';
import { useT } from '@/shared/i18n';
import { A } from '@/shared/locales/account/keys';
import { B } from '@/shared/locales/account2/keys';
import { C } from '@/shared/locales/checkout/keys';

const { eq } = window.ApperSDK.CoreSDK;

export const route = { path: '/account/receipts', layout: 'owner', access: "profile === 'Customer_c'" };
export const nav = { icon: 'Receipt', label: 'Receipts', section: 'My account', profiles: ['Customer_c'], order: 50 };

const PAGE_SIZE = 10;

function receiptPdf(p, user) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const left = 56;
  const right = 539;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(24);
  doc.text(APP_CONFIG.name, left, 78);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(120);
  doc.text(APP_CONFIG.tagline, left, 96);

  doc.setTextColor(20);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text('Receipt', left, 150);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(`Issued ${formatLocalDate(new Date().toISOString(), DATE_FORMATS.LONG)}`, right, 150, { align: 'right' });

  const lines = [
    ['Reference', p.Name || ''],
    ['Order', p.order_c?.Name || ''],
    ['Billed to', `${displayNameOf(user)} (${user?.emailAddress || ''})`],
    ['Plan', planName(p.plan_c)],
    ['Type', p.kind_c === 'Renewal' ? 'Renewal' : 'New order'],
    ['Paid with', paymentLabel(p.method_c)],
    ['Paid on', p.paid_on_c ? formatLocalDate(p.paid_on_c, DATE_FORMATS.LONG) : ''],
  ];

  let y = 190;
  doc.setDrawColor(220);
  lines.forEach(([label, value]) => {
    doc.setTextColor(120);
    doc.text(label, left, y);
    doc.setTextColor(20);
    doc.text(String(value), left + 120, y);
    y += 26;
  });

  doc.line(left, y, right, y);
  y += 30;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text('Total paid (USD)', left, y);
  doc.text(money(p.amount_c), right, y, { align: 'right' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(120);
  doc.text('All sales are final. See the refund policy on the Ghost Cable website.', left, 780);

  const slug = String(p.Name || 'receipt').replace(/[^A-Za-z0-9]+/g, '-').replace(/^-|-$/g, '');
  doc.save(`ghost-cable-${slug || 'receipt'}.pdf`);
}

function DownloadButton({ payment, user }) {
  const { t } = useT();
  const [busy, setBusy] = useState(false);

  function go() {
    setBusy(true);
    try {
      receiptPdf(payment, user);
      toast.success(t(B.pdfDone));
    } catch {
      toast.error(t(B.pdfFail));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Button variant="outline" size="sm" onClick={go} disabled={busy} aria-label={`${t(B.download)} ${payment.Name}`}>
      <ApperIcon name={busy ? 'LoaderCircle' : 'Download'} size={16} className={busy ? 'animate-spin' : undefined} />
      <span className="hidden sm:inline">{busy ? t(B.preparing) : 'PDF'}</span>
    </Button>
  );
}

export default function Receipts() {
  const { t } = useT();
  const { user } = useAuth();
  const [kind, setKind] = useState('all');
  const [page, setPage] = useState(1);

  const { data, loading, error, run } = useFetch(() => {
    let q = sdk.table('payments_c').select(PAYMENT_FIELDS);
    if (kind !== 'all') q = q.where(eq('kind_c', kind));
    return q.orderByDesc('paid_on_c').page(page, PAGE_SIZE).fetch().then(paged);
  }, [kind, page]);
  const list = data?.rows ?? [];

  function changeKind(v) {
    setKind(v);
    setPage(1);
  }

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        eyebrow={t('My account')}
        title={t(A.navReceipts)}
        description={t(B.receiptsDesc)}
      />

      <Toolbar>
        <FilterSelect value={kind} onChange={changeKind} values={PAYMENT_KINDS} allLabel={B.allPayments} />
      </Toolbar>

      <ListBody
        loading={loading}
        data={data}
        error={error}
        run={run}
        isEmpty={!list.length}
        empty={{
          icon: 'Receipt',
          title: t(B.noReceipts),
          description: t(B.noReceiptsP),
        }}
      >
        <div className="overflow-x-auto rounded-lg border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t(C.reference)}</TableHead>
                <TableHead className="hidden md:table-cell">{t(C.plan)}</TableHead>
                <TableHead className="hidden sm:table-cell">{t(B.colType)}</TableHead>
                <TableHead className="hidden lg:table-cell">{t(B.colMethod)}</TableHead>
                <TableHead className="hidden md:table-cell">{t(B.colPaidOn)}</TableHead>
                <TableHead className="text-end">{t(C.amount)}</TableHead>
                <TableHead className="w-0">
                  <span className="sr-only">{t(B.download)}</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.map((p) => {
                const orderId = idOf(p.order_c);
                return (
                  <TableRow key={p.Id}>
                    <TableCell>
                      {orderId ? (
                        <Link to={`/account/orders/${orderId}`} className="font-mono text-primary hover:underline">
                          {p.Name}
                        </Link>
                      ) : (
                        <span className="font-mono">{p.Name}</span>
                      )}
                      <p className="text-xs text-muted-foreground md:hidden">
                        {p.paid_on_c ? formatLocalDate(p.paid_on_c, DATE_FORMATS.SHORT) : '—'}
                      </p>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">{planName(p.plan_c)}</TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <StatusBadge value={p.kind_c} styles={PAYMENT_KIND_STYLES} />
                    </TableCell>
                    <TableCell className="hidden text-muted-foreground lg:table-cell">{t(paymentLabel(p.method_c))}</TableCell>
                    <TableCell className="hidden text-muted-foreground md:table-cell">
                      {p.paid_on_c ? formatLocalDate(p.paid_on_c, DATE_FORMATS.SHORT) : '—'}
                    </TableCell>
                    <TableCell className="text-end tabular-nums">{money(p.amount_c)}</TableCell>
                    <TableCell>
                      <DownloadButton payment={p} user={user} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        <Pager page={page} total={data?.total} pageSize={PAGE_SIZE} hasMore={data?.hasMore} onPage={setPage} />
      </ListBody>
    </div>
  );
}
